import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Calendar, GraduationCap, Plus, FolderOpen, Settings, AlertTriangle, Wrench, RefreshCw, FileText, X, Wallet, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const navItems = [
    { to: '/user', icon: Calendar, label: 'Calendario', exact: true, permission: 'calendario.ver' },
    { to: '/user/ope', icon: GraduationCap, label: 'OPE', permission: 'ope.ver' },
    { id: 'actions', icon: Plus, label: 'Acciones', isAction: true },
    { to: '/user/historial', icon: FolderOpen, label: 'Historial', permission: 'intercambios.ver' },
    { to: '/user/ajustes', icon: Settings, label: 'Ajustes' },
];

const actionItems = [
    { id: 'accidente', icon: AlertTriangle, label: 'Accidente', color: 'from-red-500 to-red-600', permission: 'accidentes.ver' },
    { id: 'averia', icon: Wrench, label: 'Avería', color: 'from-orange-500 to-orange-600', permission: 'averias.ver' },
    { id: 'cambio', icon: RefreshCw, label: 'Cambio', color: 'from-blue-500 to-blue-600', permission: 'intercambios.ver' },
    { id: 'ap', icon: FileText, label: 'A.P.', color: 'from-green-500 to-green-600', permission: 'asuntos_propios.ver' },
    { id: 'bolsa', icon: Wallet, label: 'Bolsa Días', color: 'from-purple-500 to-indigo-600', permission: 'calendario.ver' },
    { id: 'horas-sindicales', icon: Clock, label: 'H. Sindical', color: 'from-cyan-500 to-teal-600', permission: 'calendario.ver' },
];

const BottomNavigation = () => {
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const { hasPermission, isAdmin } = useAuth();
    const navigate = useNavigate();

    const isUserAdmin = typeof isAdmin === 'function' ? isAdmin() : false;

    const filterByPermission = (items) => {
        if (isUserAdmin) return items;
        return items.filter(item => {
            if (!item.permission) return true;
            return hasPermission(item.permission);
        });
    };

    const visibleActionItems = filterByPermission(actionItems);
    const visibleNavItems = filterByPermission(navItems)
        .filter(item => !(item.isAction && visibleActionItems.length === 0));

    const handleAction = (actionId) => {
        setIsActionsOpen(false);

        if (actionId === 'ap') {
            navigate('/user/ap');
            return;
        }

        if (actionId === 'bolsa') {
            navigate('/user/bolsa-dias');
            return;
        }

        if (actionId === 'horas-sindicales') {
            navigate('/user/horas-sindicales');
            return;
        }

        if (actionId === 'cambio') {
            navigate('/user/cambios');
            return;
        }

        if (actionId === 'averia') {
            navigate('/user/averia');
            return;
        }

        if (actionId === 'accidente') {
            navigate('/user/accidentes');
            return;
        }
    };

    return (
        <>
            {/* Actions Modal */}
            {isActionsOpen && visibleActionItems.length > 0 && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                        onClick={() => setIsActionsOpen(false)}
                    />

                    {/* Actions Panel */}
                    <div className="fixed bottom-20 left-0 right-0 z-50 px-4 pb-4">
                        <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--glass-border)] p-4 max-w-sm mx-auto shadow-2xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Acciones rápidas</h3>
                                <button
                                    onClick={() => setIsActionsOpen(false)}
                                    className="p-1 rounded-full hover:bg-[var(--glass-bg)] transition-colors"
                                >
                                    <X className="w-5 h-5 text-[var(--text-muted)]" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {visibleActionItems.map((action) => (
                                    <button
                                        key={action.id}
                                        onClick={() => handleAction(action.id)}
                                        className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br ${action.color} transition-transform hover:scale-105 active:scale-95`}
                                    >
                                        <action.icon className="w-5 h-5 text-white" />
                                        <span className="text-sm font-medium text-white">{action.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Bottom Navigation Bar */}
            <nav className="fixed bottom-0 left-0 right-0 bg-[var(--bg-secondary)] border-t border-[var(--glass-border)] z-50 safe-area-bottom shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
                <div className="flex justify-around items-center h-16 max-w-lg mx-auto">
                    {visibleNavItems.map((item) => (
                        item.isAction ? (
                            <button
                                key={item.id}
                                onClick={() => setIsActionsOpen(!isActionsOpen)}
                                className="relative -mt-6"
                            >
                                <div className={`w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 ring-4 ring-[var(--bg-primary)] transition-transform ${isActionsOpen ? 'rotate-45' : ''}`}>
                                    <item.icon className="w-6 h-6 text-white" />
                                </div>
                            </button>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.exact}
                                className={({ isActive }) => `
                                    flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-200
                                    ${isActive ? 'text-cyan-400' : ''}
                                `}
                                style={({ isActive }) => ({ color: isActive ? 'var(--accent-label)' : 'var(--text-secondary)' })}
                            >
                                <item.icon className="w-5 h-5 mb-1" />
                                <span className="text-[10px] font-medium">{item.label}</span>
                            </NavLink>
                        )
                    ))}
                </div>
            </nav>
        </>
    );
};

export default BottomNavigation;
